import React, { useState, useEffect } from 'react';
import Sidebar from '../Sidebar';
import { ArrowRight, ArrowLeft, HardHat, LayoutDashboard, ClipboardCheck, Building2, Cog, Clock } from 'lucide-react';
import HelpCenter from '../HelpCenter';
import ModuloHeader from '../ModuloHeader';
import CertificacaoForms from './certificacao/CertificacaoForms';
import ConservacaoForms from './conservacao/ConservacaoForms';
import CalendarioManutencao from './agenda/CalendarioManutencao';
import useEhCompacto from '../../utils/useEhCompacto';
import { perfilNoModulo } from '../../utils/perfisDerivados';

// =====================================================================
// Hub do Módulo Manutenção. Paleta indigo (#4f46e5).
//
// ⚠️ A Visão Geral tem DOIS níveis desde 2026-09-03, e essa é a decisão de
// desenho: os cards do miolo são as FRENTES da manutenção (Operacional,
// Equipamento e Conservação), e cada frente abre um
// sub-hub com os PROGRAMAS que rodam dentro dela. Antes o card era o próprio
// programa — o que não aguenta um segundo programa na mesma frente (é o que
// vem por aí). O clique extra é o preço aceito para a frente ter lugar próprio.
//
// Hoje DUAS frentes têm programa: Equipamento (Certificação de Equipamentos) e
// Conservação (Conservação Predial). A Operacional segue com o selo "Em breve"
// — marca o lugar, não navega (sub-hub vazio é beco sem saída).
//
// ⚠️⚠️ A **SOLICITAÇÃO DE REPARO SAIU DAQUI EM 10/09**, e com ela a Retirada de
// Equipamento (que tinha sido este mesmo programa, com outro nome, entre 09/09 e
// 10/09). As duas viraram o módulo **LOGÍSTICA** (`/logistica`): são o mesmo
// pedido — algo sai da unidade e volta —, e lá a EQUIPE escolhida decide se o
// que sai é o equipamento (T.I) ou as peças (Manutenção).
// ⚠️ Quem procurar `solicitacao-reparo` neste arquivo encontra este comentário:
// é o de-para. O `?tab=solicitacao-reparo` de quem tinha a tela nos favoritos
// deixa de casar e cai na Visão Geral — aceito, porque o programa mudou de
// módulo e não há para onde redirecionar dentro deste hub.
// =====================================================================

const COR = '#4f46e5';

// Quem vê o CALENDÁRIO de programação. ⚠️ Ele saiu da Visão Geral e passou a
// ficar DENTRO da frente do seu assunto (`tipoAgenda` de cada categoria): o de
// Certificação em "Manutenção Equipamento", o de Conservação em "Conservação".
// Lista própria (e não a do programa) porque ver a programação e usar o
// programa são coisas diferentes — o Gerente lê o calendário da própria loja
// mesmo onde só consulta. ⚠️ Quem AGENDA é um conjunto menor
// (Admin/Manutencao/Diretoria) e essa regra mora no backend.
const PERFIS_CALENDARIO = ['Admin', 'Manutencao', 'Diretoria', 'Gerente', 'Operacional'];

// Quem vê as frentes ainda sem programa (selo "Em breve"). É de propósito um
// conjunto menor do que o do módulo: quem toca o roadmap enxerga o que vem,
// a loja não recebe promessa de tela que ainda não existe.
const PERFIS_EM_BREVE = ['Admin', 'Manutencao', 'Diretoria', 'Operacional'];

// Os programas — as telas de verdade. `perfis` é o recorte de quem entra.
// ⚠️ O Gerente entra nos dois só para consulta/validação da própria loja; o
// recorte fino (o que ele vê dentro) mora no sub-hub de cada programa.
const PROGRAMAS = {
  'certificacao': {
    label: 'Programa de Certificação de Equipamentos',
    desc: 'Relatório de Certificação Anual de Equipamento (Etapa 1 Diagnóstico → Etapa 2), com validação do gerente e do consultor.',
    icon: ClipboardCheck,
    perfis: ['Manutencao', 'Admin', 'Diretoria', 'Gerente', 'Operacional'],
    ajuda: 'certificacao-equipamentos',
  },
  'conservacao-predial': {
    label: 'Programa Anual de Conservação Predial',
    desc: 'Inspeção anual do prédio da unidade, com ICP, Ordens de Serviço e certificação.',
    icon: Building2,
    perfis: ['Manutencao', 'Admin', 'Diretoria', 'Gerente', 'Operacional'],
    ajuda: 'conservacao-predial',
  },
};

// As frentes da manutenção. ⚠️ `larguraTotal` continua no desenho (é o que põe
// uma frente numa linha só dela, embaixo das outras) mas hoje NENHUMA a usa —
// a que usava era a Solicitação de Reparo, que virou o módulo Logística. O
// mecanismo fica: a próxima frente que não couber na grade o reaproveita.
const CATEGORIAS = [
  {
    id: 'operacional',
    eyebrow: 'Operação',
    label: 'Manutenção Operacional',
    desc: 'Rotinas de manutenção conduzidas pela própria operação da loja.',
    icon: Cog,
    programas: [],
    emBreve: true,
  },
  {
    id: 'equipamento',
    eyebrow: 'Equipamentos',
    label: 'Manutenção Equipamento',
    desc: 'Certificação e acompanhamento do parque de equipamentos da rede.',
    icon: ClipboardCheck,
    programas: ['certificacao'],
    // O calendário desta frente. É o mesmo componente das duas, recortado por
    // tipo — ver CalendarioManutencao.js.
    tipoAgenda: 'Certificacao',
  },
  {
    id: 'conservacao',
    eyebrow: 'Prédio',
    label: 'Conservação',
    desc: 'Estado de conservação do prédio da unidade — o imóvel, não o equipamento.',
    icon: Building2,
    programas: ['conservacao-predial'],
    tipoAgenda: 'Conservacao',
  },
];

// Programas de uma frente que o perfil alcança.
const programasVisiveis = (cat, userRole) =>
  cat.programas.filter(id => PROGRAMAS[id].perfis.includes(userRole) || userRole === 'Admin');

// Frente visível: a que tem ao menos um programa alcançável — ou, se ainda não
// tem programa nenhum, a que o perfil pode ver como "Em breve".
const categoriaVisivel = (cat, userRole) => (
  cat.emBreve
    ? (PERFIS_EM_BREVE.includes(userRole) || userRole === 'Admin')
    : programasVisiveis(cat, userRole).length > 0
);

// De qual frente é este programa (usado para marcar a aba certa quando a pessoa
// está dentro de uma tela).
const categoriaDoPrograma = idPrograma => CATEGORIAS.find(c => c.programas.includes(idPrograma));

const ManutencaoPainel = () => {
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : {};
  const userRole = perfilNoModulo(user?.perfil, 'manutencao') || '';

  const [isMobile] = useState(window.innerWidth <= 768);
  const compacto = useEhCompacto(); // acompanha o resize (celular/tablet) p/ afinar paddings
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
  // activeTab guarda 'dashboard', o id de uma FRENTE (sub-hub) ou o id de um
  // PROGRAMA (a tela). Os ids não se cruzam de propósito — por isso a frente é
  // 'conservacao' e o programa é 'conservacao-predial'.
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const categoriasVisiveis = CATEGORIAS.filter(cat => categoriaVisivel(cat, userRole));

  // ---------------------------------------------------------------
  // Card do miolo — serve tanto para a frente (Visão Geral) quanto para o
  // programa (sub-hub). Muda só o texto e o que acontece no clique.
  // ---------------------------------------------------------------
  const CardAcao = ({ eyebrow, label, desc, icon: Icone, onClick, emBreve, chamada = 'Acessar' }) => (
    <div
      onClick={emBreve ? undefined : onClick}
      style={{
        backgroundColor: '#fff', borderRadius: '16px', padding: '24px',
        border: '1px solid #e0e7ff', display: 'flex', flexDirection: 'column',
        height: '100%', cursor: emBreve ? 'default' : 'pointer',
        position: 'relative', overflow: 'hidden', transition: 'all 0.2s',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
        opacity: emBreve ? 0.7 : 1,
      }}
      onMouseEnter={e => {
        if (emBreve) return;
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 8px 20px -2px rgba(0,0,0,0.1)';
      }}
      onMouseLeave={e => {
        if (emBreve) return;
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.05)';
      }}
    >
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', backgroundColor: emBreve ? '#c7d2fe' : COR }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <p style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: COR, marginBottom: '8px', margin: 0 }}>{eyebrow}</p>
          <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: 0 }}>{label}</h3>
        </div>
        <div style={{ padding: '12px', borderRadius: '12px', backgroundColor: '#e0e7ff', color: COR }}>
          <Icone size={26} />
        </div>
      </div>
      <div style={{ marginTop: 'auto' }}>
        <p style={{ fontSize: '14px', color: '#64748b', fontWeight: '500', lineHeight: '1.5', margin: '0 0 16px 0' }}>{desc}</p>
        {emBreve ? (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px',
            fontWeight: '700', color: '#64748b', backgroundColor: '#f1f5f9',
            border: '1px solid #e2e8f0', borderRadius: '999px', padding: '5px 12px',
          }}>
            <Clock size={13} /> Em breve
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '700', color: COR }}>
            <span>{chamada}</span> <ArrowRight size={16} />
          </div>
        )}
      </div>
    </div>
  );

  const OverviewWidgets = () => {
    const naLinha = categoriasVisiveis.filter(c => !c.larguraTotal);
    const embaixo = categoriasVisiveis.filter(c => c.larguraTotal);

    return (
      <div>
        <div style={{
          background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
          borderRadius: '16px', padding: compacto ? '24px' : '40px', color: '#fff',
          marginBottom: '32px', position: 'relative', overflow: 'hidden',
          boxShadow: '0 10px 15px -3px rgba(79, 70, 229, 0.3)',
        }}>
          <div style={{ position: 'relative', zIndex: 10 }}>
            <h2 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '12px', marginTop: 0, color: '#fff' }}>
              Manutenção
            </h2>
            <p style={{ color: '#e0e7ff', fontSize: '18px', maxWidth: '650px', margin: 0, lineHeight: '1.6' }}>
              Ferramentas da equipe de manutenção e restaurante do Grupo Correia MC.
            </p>
          </div>
          <div style={{ position: 'absolute', right: '-30px', top: '-40px', opacity: 0.12 }}>
            <HardHat size={250} color="#ffffff" />
          </div>
        </div>

        {/* As três primeiras frentes na MESMA linha. `auto-fit` com três itens
            não passa de três colunas — e, se o perfil vê menos frentes, elas
            se esticam em vez de deixar buraco na grade. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
          {naLinha.map(cat => (
            <CardAcao
              key={cat.id}
              eyebrow={cat.eyebrow}
              label={cat.label}
              desc={cat.desc}
              icon={cat.icon}
              emBreve={cat.emBreve}
              onClick={() => setActiveTab(cat.id)}
            />
          ))}
        </div>

        {/* A linha de largura total. ⚠️ HOJE NÃO HÁ NENHUMA frente com
            `larguraTotal` (a que havia virou o módulo Logística), então este
            bloco não desenha nada — o `embaixo.length > 0` cuida disso. Fica
            porque o mecanismo é do LAYOUT, não daquela frente. */}
        {embaixo.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '24px', marginTop: '24px' }}>
            {embaixo.map(cat => (
              <CardAcao
                key={cat.id}
                eyebrow={cat.eyebrow}
                label={cat.label}
                desc={cat.desc}
                icon={cat.icon}
                emBreve={cat.emBreve}
                onClick={() => setActiveTab(cat.id)}
              />
            ))}
          </div>
        )}

      </div>
    );
  };

  // Sub-hub de uma frente: lista os programas que rodam dentro dela.
  const SubHubCategoria = ({ cat }) => {
    const programas = programasVisiveis(cat, userRole);
    return (
      <div>
        <button
          onClick={() => setActiveTab('dashboard')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px', border: 'none',
            background: 'transparent', color: '#64748b', fontWeight: '600',
            fontSize: '14px', cursor: 'pointer', padding: 0, marginBottom: '16px',
          }}
        >
          <ArrowLeft size={16} /> Visão Geral
        </button>

        <div style={{ marginBottom: '24px' }}>
          <p style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: COR, margin: '0 0 6px 0' }}>
            {cat.eyebrow}
          </p>
          <h2 style={{ fontSize: '26px', fontWeight: '800', color: '#0f172a', margin: '0 0 8px 0' }}>{cat.label}</h2>
          <p style={{ fontSize: '15px', color: '#64748b', margin: 0, maxWidth: '720px', lineHeight: '1.6' }}>{cat.desc}</p>
        </div>

        {programas.length === 0 ? (
          <div style={{
            backgroundColor: '#fff', borderRadius: '16px', border: '1px dashed #c7d2fe',
            padding: '40px', textAlign: 'center', color: '#64748b',
          }}>
            <Clock size={28} color={COR} />
            <p style={{ fontWeight: '700', color: '#0f172a', margin: '12px 0 4px 0' }}>Nenhum programa publicado ainda</p>
            <p style={{ fontSize: '14px', margin: 0 }}>Esta frente está sendo construída.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
            {programas.map(id => (
              <CardAcao
                key={id}
                eyebrow="Programa"
                label={PROGRAMAS[id].label}
                desc={PROGRAMAS[id].desc}
                icon={PROGRAMAS[id].icon}
                chamada="Abrir"
                onClick={() => setActiveTab(id)}
              />
            ))}
          </div>
        )}

        {/* Calendário de programação da frente — quando o serviço DELA vai
            acontecer, e se já aconteceu. Fica aqui (e não na Visão Geral, onde
            nasceu) porque a pergunta "o que está atrasado?" é do assunto, não
            do módulo: quem cuida da certificação não precisa varrer a agenda da
            conservação para achar a sua. ⚠️ O componente é o mesmo nas duas
            frentes — o recorte é a prop `tipo`. */}
        {cat.tipoAgenda && PERFIS_CALENDARIO.includes(userRole) && (
          <div style={{
            backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e0e7ff',
            padding: compacto ? '16px' : '24px', marginTop: '24px',
          }}>
            <CalendarioManutencao tipo={cat.tipoAgenda} />
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    const handleResize = () => { if (window.innerWidth <= 768) setIsCollapsed(true); };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Portão de exibição: perfil que não alcança a aba aberta volta para a Visão
  // Geral. Vale para o programa (recorte de perfil) e para a frente (visível?).
  useEffect(() => {
    if (activeTab === 'dashboard') return;
    const programa = PROGRAMAS[activeTab];
    if (programa) {
      if (!(programa.perfis.includes(userRole) || userRole === 'Admin')) setActiveTab('dashboard');
      return;
    }
    const cat = CATEGORIAS.find(c => c.id === activeTab);
    if (!cat || !categoriaVisivel(cat, userRole)) setActiveTab('dashboard');
  }, [activeTab, userRole]);

  const contentBoxStyle = {
    backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e0e7ff',
    padding: compacto ? '16px' : '32px', minHeight: '75vh', width: '100%',
  };

  const renderContent = () => {
    if (activeTab === 'dashboard') return <OverviewWidgets />;

    // Telas dos programas. A Certificação recebe o destino da VOLTA — a frente
    // dona dela —, porque com o hub em dois níveis a aba sozinha não basta: quem
    // entrou pelo card do programa espera desfazer o último passo, não pular
    // para a Visão Geral. ⚠️ O rótulo vai junto de propósito: seta sem destino
    // escrito ("←" pelado) não diz para onde leva.
    if (activeTab === 'certificacao') {
      const dona = categoriaDoPrograma('certificacao');
      return (
        <div style={contentBoxStyle}>
          <CertificacaoForms
            onVoltar={() => setActiveTab(dona?.id || 'dashboard')}
            rotuloVoltar={dona?.label}
          />
        </div>
      );
    }
    if (activeTab === 'conservacao-predial') return <div style={contentBoxStyle}><ConservacaoForms /></div>;

    const cat = CATEGORIAS.find(c => c.id === activeTab);
    if (cat) return <SubHubCategoria cat={cat} />;
    return null;
  };

  const getMarginLeft = () => window.innerWidth <= 768 ? '0px' : (isCollapsed ? '80px' : '260px');

  // Por qual verbete o Manual da Tela abre — o do programa aberto; nas frentes
  // e na Visão Geral, o panorama do módulo.
  const getSecaoAjuda = () => PROGRAMAS[activeTab]?.ajuda || 'visao-geral-manutencao';

  // Qual aba fica marcada: a frente aberta ou, se a pessoa está dentro de um
  // programa, a frente dona dele.
  const abaMarcada = PROGRAMAS[activeTab] ? categoriaDoPrograma(activeTab)?.id : activeTab;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8f8f8' }}>
      <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} mostrarBotaoMobile={false} />
      <div style={{ flex: 1, marginLeft: getMarginLeft(), transition: 'margin-left 0.3s ease' }}>

        <ModuloHeader
          eyebrow="Módulo"
          titulo="Manutenção"
          cor={COR}
          onAbrirManual={() => setIsHelpOpen(true)}
          onAbrirMenu={() => setIsCollapsed(false)}
          isMobile={isMobile}
        />

        <div style={{ padding: compacto ? '16px' : '40px', maxWidth: '1600px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

          {/* BARRA DE NAVEGAÇÃO — espelha as frentes dos cards. A frente sem
              programa fica desabilitada (não há para onde ir). */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '32px',
            backgroundColor: '#fff', padding: '8px', borderRadius: '12px',
            border: '1px solid #e0e7ff', width: '100%',
          }}>
            <button
              onClick={() => setActiveTab('dashboard')}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 20px', borderRadius: '8px', border: 'none',
                fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
                background: activeTab === 'dashboard' ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' : 'transparent',
                color: activeTab === 'dashboard' ? '#fff' : '#64748b',
                boxShadow: activeTab === 'dashboard' ? '0 4px 6px rgba(79, 70, 229, 0.3)' : 'none',
                whiteSpace: 'nowrap',
              }}
            >
              <LayoutDashboard size={18} /> Visão Geral
            </button>

            <div style={{ minWidth: '1px', backgroundColor: '#e0e7ff', margin: '0 5px' }} />

            {categoriasVisiveis.map(cat => {
              const ativa = abaMarcada === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => !cat.emBreve && setActiveTab(cat.id)}
                  disabled={cat.emBreve}
                  title={cat.emBreve ? 'Em breve' : undefined}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '10px 20px', borderRadius: '8px', fontWeight: '600',
                    cursor: cat.emBreve ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
                    border: ativa ? `2px solid ${COR}` : '2px solid transparent',
                    backgroundColor: ativa ? '#e0e7ff' : 'transparent',
                    color: cat.emBreve ? '#94a3b8' : (ativa ? COR : '#64748b'),
                    whiteSpace: 'nowrap',
                  }}
                >
                  <cat.icon size={18} /> {cat.label}
                </button>
              );
            })}
          </div>

          {renderContent()}
        </div>
      </div>

      {/* Manual da Tela — verbetes do módulo (HelpCenter › manualManutencao).
          Abre já no verbete do programa aberto; fora deles, no panorama. */}
      <HelpCenter
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        modulo="manutencao"
        secaoInicial={getSecaoAjuda()}
      />
    </div>
  );
};

export default ManutencaoPainel;
